import { useState, useEffect, useRef, useMemo } from "react";
import Fuse from "fuse.js";

interface SearchableInputProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  emptyMessage?: string;
  allowCustom?: boolean;
  showCount?: boolean;
}

export default function SearchableInput({
  title,
  value,
  onChange,
  options,
  placeholder = "Type or select...",
  loading = false,
  disabled = false,
  className = "",
  emptyMessage = "No options available",
  allowCustom = true,
  showCount = true,
}: SearchableInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync input value with prop value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Initialize Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(options, {
      threshold: 0.3,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 1
    });
  }, [options]);

  // Filter options using fuzzy search
  const filteredOptions = useMemo(() => {
    if (!inputValue) return options;

    const results = fuse.search(inputValue);
    return results.map(result => result.item);
  }, [fuse, inputValue, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1,
          );
          break;
        case "Enter":
          event.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length
          ) {
            handleSelect(filteredOptions[highlightedIndex]);
          } else if (allowCustom && inputValue.trim()) {
            handleSelect(inputValue.trim());
          }
          break;
        case "Escape":
          event.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, filteredOptions, highlightedIndex, inputValue, allowCustom]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setInputValue(selectedValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onChange(newValue);
    if (!isOpen && newValue.length > 0) {
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    if (!disabled && !loading) {
      setIsOpen(true);
    }
  };

  const ChevronDownIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );

  const CheckIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-xs font-medium text-text-dark mb-2">
        {title}
        {showCount && options.length > 0 && (
          <span className="ml-1 text-text-dark/50">({options.length})</span>
        )}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          disabled={disabled || loading}
          placeholder={placeholder}
          className={`
            w-full px-3 py-2.5 pr-10 text-sm border border-secondary-300 rounded-lg
            bg-background text-text-dark placeholder-text-dark/50
            focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary
            transition-all duration-200
            ${
              disabled || loading
                ? "opacity-50 cursor-not-allowed bg-gray-50"
                : "hover:border-primary-400"
            }
            ${isOpen ? "ring-2 ring-primary-200 border-primary" : ""}
          `}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          ) : (
            <div className={`text-text-dark/40 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
              <ChevronDownIcon />
            </div>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-56 overflow-hidden ring-1 ring-black/5">
          {/* Options List */}
          <div className="p-2 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {filteredOptions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="w-8 h-8 text-text-dark/30 mb-3">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                  </svg>
                </div>
                <p className="text-sm text-text-dark/60 text-center font-medium">
                  {inputValue
                    ? `No matches for "${inputValue}"`
                    : emptyMessage}
                </p>
                {allowCustom && inputValue.trim() && (
                  <button
                    onClick={() => handleSelect(inputValue.trim())}
                    className="mt-2 px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary-700 transition-colors"
                  >
                    Use "{inputValue.trim()}"
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredOptions.map((option, index) => {
                  const isSelected = option === value;
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <button
                      key={option}
                      onClick={() => handleSelect(option)}
                      className={`
                        group w-full px-3 py-2 text-left text-sm transition-all duration-200
                        flex items-center justify-between border border-transparent rounded-md
                        hover:bg-primary-50 hover:border-primary/10 cursor-pointer
                        ${
                          isHighlighted
                            ? "bg-primary-50 text-primary border-primary/20"
                            : "text-text-dark"
                        }
                        ${isSelected ? "bg-primary-100 border-primary/30 text-primary font-medium" : ""}
                      `}
                    >
                      <span className="truncate group-hover:text-primary transition-colors duration-200">
                        {option}
                      </span>
                      {isSelected && (
                        <div className="text-primary flex-shrink-0 ml-2">
                          <CheckIcon />
                        </div>
                      )}
                    </button>
                  );
                })}
                {allowCustom && inputValue.trim() && !filteredOptions.includes(inputValue.trim()) && (
                  <button
                    onClick={() => handleSelect(inputValue.trim())}
                    className="w-full px-3 py-2 text-left text-sm border border-dashed border-primary/30 rounded-md text-primary hover:bg-primary-50 transition-colors"
                  >
                    <span className="font-medium">Use custom: "{inputValue.trim()}"</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer with count */}
          {filteredOptions.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-center text-xs text-text-dark/60">
                <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 17.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
                <span className="font-medium">
                  {filteredOptions.length} of {options.length} options
                  {inputValue && ` matching "${inputValue}"`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}